import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import QuickActionsSidebar from "./QuickActionsSidebar";
import {
  getDistributorActions,
  getAdminActions,
} from "../../../config/actions";

import { API_URL } from "../../../config";

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import GrantModuleModal from "./GrantModuleModal";
import Pagination from "../../common/Pagination";

import "../../css/QuickActions.css";
import "./ManageCustomers.css";

const ManageCustomers = () => {
  const navigate = useNavigate();

  const userId = localStorage.getItem("user_id");
  const userType = localStorage.getItem("user_type");

  const isAdmin =
    userType === "7" || userType === "8";

  const isDistributor = userType === "6";

  if (!isAdmin && !isDistributor) {
    navigate("/");
    return null;
  }

  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] =
    useState([]);
  const CUSTOMER_PAGE_SIZE = 5;
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const customerTotalPages = Math.max(
    1,
    Math.ceil(filteredCustomers.length / CUSTOMER_PAGE_SIZE)
  );
  const paginatedCustomers = filteredCustomers.slice(
    (customerCurrentPage - 1) * CUSTOMER_PAGE_SIZE,
    customerCurrentPage * CUSTOMER_PAGE_SIZE
  );

  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [sortField, setSortField] =
    useState("name");

  const [sortOrder, setSortOrder] =
    useState("asc");

  const [filterSystem, setFilterSystem] =
    useState("all");

  const [filterModule, setFilterModule] =
    useState("all");

  const [filterDistributor, setFilterDistributor] =
    useState("all");

  const [filterSite, setFilterSite] =
    useState("all");

  const [distributors, setDistributors] =
    useState([]);

  const [orderDetailsModal, setOrderDetailsModal] =
    useState(false);

  const [selectedOrder, setSelectedOrder] =
    useState(null);

  const [grantModuleModal, setGrantModuleModal] =
    useState(false);

  const [grantModuleCustomer, setGrantModuleCustomer] =
    useState(null);

  const [expandedRegistration, setExpandedRegistration] =
    useState(0);

  const [showPassword, setShowPassword] =
    useState(false);

  const [expandedMenu, setExpandedMenu] =
    useState(null);

  const paypalButtonRef = useRef();

  const [allSystem, setAllSystem] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [sites, setSites] = useState([]);

  // =========================
  // HELPERS
  // =========================
  const formatTimestamp = (ts) => {
    if (!ts) return "N/A";
    return new Date(ts * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // =========================
  // FETCH SYSTEMS
  // =========================
  const fetchAllSystem = async () => {
    try {
      const response = await fetch(
        `${API_URL}/get/specified/product/by/22`
      );

      const res = await response.json();

      const defaultSystems = (res.data || []).filter(
        (item) => item.id >= 130 && item.id <= 134
      );

      setAllSystem(defaultSystems);
    } catch (error) {
      console.log(error);
    }
  };

  // =========================
  // FETCH MODULES
  // =========================
  const fetchModules = async () => {
    try {
      const response = await fetch(
        `${API_URL}/get/all/modules`
      );

      const res = await response.json();

      if (response.status === 200) {
        setAllModules(res?.modules || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // =========================
  // FETCH SITES
  // =========================
  const fetchSites = async () => {
    try {
      const response = await fetch(
        `${API_URL}/distributor/site/${userId}`,
        { headers: { "x-access-token": localStorage.getItem("accessToken") } }
      );

      const res = await response.json();

      if (res.status && Array.isArray(res.data)) {
        setSites(res.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // =========================
  // FETCH DISTRIBUTORS (admin filter dropdown)
  // =========================
  const fetchDistributors = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(`${API_URL}/admin/distributors`, {
        headers: { "x-access-token": token },
      });
      const data = await res.json();
      setDistributors(data.distributors || []);
    } catch (err) {
      console.error("Error fetching distributors:", err);
    }
  };

  // =========================
  // INITIAL LOAD
  // =========================
  useEffect(() => {
    fetchCustomers();
    fetchAllSystem();
    fetchModules();
    if (!isAdmin) {
      fetchSites();
    } else {
      fetchDistributors();
    }
  }, []);

  // =========================
  // APPLY FILTERS
  // =========================
  useEffect(() => {
    applyFiltersAndSort();
  }, [
    customers,
    searchTerm,
    sortField,
    sortOrder,
    filterSystem,
    filterModule,
    filterDistributor,
    filterSite,
  ]);

  // =========================
  // FETCH CUSTOMERS
  // =========================
  const fetchCustomers = async () => {
    try {
      setLoading(true);

      const token =
        localStorage.getItem("accessToken");

      // Admin sees every distributor's customers in one call; a real
      // distributor stays scoped to their own id, same as before.
      const customerUrl = isAdmin
        ? `${API_URL}/admin/customers`
        : `${API_URL}/all-customer-details/distributor/${userId}`;

      const customerResponse = await fetch(customerUrl, {
        headers: {
          "x-access-token": token,
        },
      });

      const customerRes =
        await customerResponse.json();

      // =========================
      // FORMAT DATA — one row per customer
      // =========================
      const allCustomers = [];

      (customerRes.data || []).forEach(
        (item) => {
          const customer = item.customer || {};

          // Skip customers with no real email on file — nothing to
          // show/contact, so don't clutter the list with them.
          if (!customer.email) {
            return;
          }

          const registrations = item.registrations || [];

          // Use first registration + its device cart item (category 22) for
          // table display columns -- cart_details can also contain accessory
          // items (e.g. CapnoSensor, category 24), so index 0 isn't reliably
          // the device.
          const firstReg = registrations[0] || {};
          const firstRegCarts = firstReg.cart_details || [];
          const firstCart =
            firstRegCarts.find((c) => Number(c.product?.category) === 22) ||
            firstRegCarts[0] ||
            null;

          // Collect all module_ids across all registrations for filter
          const allModuleIds = [];
          registrations.forEach((reg) => {
            (reg.cart_details || []).forEach((cart) => {
              (cart.modules || []).forEach((m) => {
                allModuleIds.push(String(m.id));
              });
            });
          });

          allCustomers.push({
            customer_id: customer.id || "",
            owner_id: customer.id || "",
            ownerfirstname: customer.firstname || "",
            ownerlastname: customer.lastname || "",
            email: customer.email || "",
            phone: customer.telephone || "",
            password: customer.password || "",
            gender: customer.gender || "",
            city: customer.city || "",
            state: (customer.state && !customer.state.toLowerCase().includes("choose")) ? customer.state : "",
            country: customer.country || "",
            business: customer.business || "",
            zipcode: customer.zipcode || "",
            address2: customer.address2 || "",
            address1: customer.address || "",
            serial_key: firstReg.serial_key || "",
            internal_id: firstReg.internal_id || "",
            distributer: firstReg.distributor_id || "",
            order_id: firstReg.order_id || "",
            site_name: firstReg.site_name || "—",
            eid: firstReg.id || "",
            date_activated: firstReg.date_activated || "",
            expires_at: firstReg.expires_at || "",
            device_type: firstCart?.product?.name || "",
            device_type_id: firstCart?.product?.id || "",
            modules: firstCart?.modules?.length > 0
              ? firstCart.modules.map((m) => m.name).join(", ")
              : "",
            module_ids: allModuleIds,
            quantity: firstCart?.quantity || 0,
            unit_price: firstCart?.unit_price || 0,
            total_price: firstCart?.total_price || 0,
            product: firstCart?.product || {},
            cart_details: firstReg.cart_details || [],
            allRegistrations: registrations,
            rawCustomer: customer,
          });
        }
      );


      setCustomers(allCustomers);
    } catch (error) {
      console.error(
        "Error fetching customers:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // FILTER + SORT
  // =========================
  const applyFiltersAndSort = () => {
    let filtered = [...customers];

    // SEARCH
    if (searchTerm) {
      filtered = filtered.filter(
        (customer) => {
          const ownerName =
            `${customer.ownerfirstname || ""} ${
              customer.ownerlastname || ""
            }`.toLowerCase();

          return (
            ownerName.includes(
              searchTerm.toLowerCase()
            ) ||
            (customer.email || "")
              .toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              ) ||
            (customer.phone || "")
              .toLowerCase()
              .includes(
                searchTerm.toLowerCase()
              )
          );
        }
      );
    }

    // SYSTEM FILTER
    if (filterSystem !== "all") {
      filtered = filtered.filter(
        (c) =>
          String(c.device_type_id) ===
          String(filterSystem)
      );
    }

    // MODULE FILTER
    if (filterModule !== "all") {
      filtered = filtered.filter((c) =>
        c.module_ids.includes(
          String(filterModule)
        )
      );
    }

    // DISTRIBUTOR FILTER
    if (
      isAdmin &&
      filterDistributor !== "all"
    ) {
      filtered = filtered.filter(
        (c) =>
          String(c.distributer) ===
          String(filterDistributor)
      );
    }

    // SITE FILTER
    if (filterSite !== "all") {
      filtered = filtered.filter(
        (c) => (c.site_name || "—") === filterSite
      );
    }

    // SORTING
    filtered.sort((a, b) => {
      let aVal = "";
      let bVal = "";

      if (sortField === "name") {
        aVal =
          `${a.ownerfirstname || ""} ${
            a.ownerlastname || ""
          }`.toLowerCase();

        bVal =
          `${b.ownerfirstname || ""} ${
            b.ownerlastname || ""
          }`.toLowerCase();
      }

      if (sortField === "email") {
        aVal = (
          a.email || ""
        ).toLowerCase();

        bVal = (
          b.email || ""
        ).toLowerCase();
      }

      if (sortOrder === "asc") {
        return aVal > bVal
          ? 1
          : aVal < bVal
          ? -1
          : 0;
      } else {
        return aVal < bVal
          ? 1
          : aVal > bVal
          ? -1
          : 0;
      }
    });

    setFilteredCustomers(filtered);
    setCustomerCurrentPage(1);
  };

  // =========================
  // MODAL
  // =========================
  const handleViewCustomerDetails = (
    customer
  ) => {
    setSelectedOrder(customer);
    setExpandedRegistration(0);
    setShowPassword(false);
    setOrderDetailsModal(true);
  };

  const toggleOrderDetailsModal = () => {
    setOrderDetailsModal(
      !orderDetailsModal
    );

    if (orderDetailsModal) {
      setSelectedOrder(null);
    }
  };

  const handleGrantModule = (customer) => {
    setGrantModuleCustomer(customer);
    setGrantModuleModal(true);
  };

  const toggleGrantModuleModal = () => {
    setGrantModuleModal(!grantModuleModal);
    if (grantModuleModal) {
      setGrantModuleCustomer(null);
    }
  };

  // =========================
  // ACTIONS
  // =========================
  const actions = isAdmin
    ? getAdminActions("7", userId)
    : getDistributorActions(userId);

  const siteOptions = sites.map((s) => s.name);

  return (
    <div className="manage-customers-container">
      <div className="customers-dashboard">
        <QuickActionsSidebar
          actions={actions}
          expandedMenu={expandedMenu}
          setExpandedMenu={
            setExpandedMenu
          }
        />

        <div className="customers-content">
          {/* HEADER */}
          <div className="content-header">
            <div className="header-content">
              <h1>
                Manage Customers
              </h1>

              <p>
                View and manage all your
                customers
              </p>
            </div>
          </div>

          {/* DISTRIBUTOR SCOPE — prominent standalone bar, same
              placement/role as the Dashboard page's distributor filter */}
          {isAdmin && (
            <div className="customer-distributor-bar" style={{margin:0}}>
              <div className="customer-filter-group">
                <label style={{margin:0}}>Filter Distributor:</label>
                <select
                  value={filterDistributor}
                  onChange={(e) =>
                    setFilterDistributor(e.target.value)
                  }
                >
                  <option value="all">All Distributors</option>
                  {distributors.map((dist) => (
                     <option key={dist.id} value={dist.distributor_id}>
                      {`${dist.firstname || ""} ${dist.lastname || ""}`.trim() ||
                        dist.business ||
                        `Distributor #${dist.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* FILTERS */}
          <div className="filters-section">
            <div className="customer-sort-group">

              <label>Sort by:</label>
              <div className="sort-controls-inline">
                <select
                  value={sortField}
                  onChange={(e) =>
                    setSortField(
                      e.target.value
                    )
                  }
                >
                  {/* <option value="email">
                    Email
                  </option> */}

                  <option value="name">
                    Name
                  </option>
                </select>

                <button
                  className={`sort-order-btn ${sortOrder}`}
                  onClick={() =>
                    setSortOrder(
                      sortOrder === "asc"
                        ? "desc"
                        : "asc"
                    )
                  }
                >
                  {sortOrder === "asc"
                    ? "ASC"
                    : "DESC"}
                </button>
              </div>
            </div>

            {/* SEARCH */}
            <div className="search-box">
              <i className="fas fa-search"></i>

              <input
                type="text"
                placeholder="Search by name, email or phone..."
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(
                    e.target.value
                  )
                }
              />
            </div>

            {/* FILTER HEADER */}
            <div className="customer-filters-header">
              <span className="customer-filters-label">
                Filters
              </span>

              <button
                className="customer-clear-filters-btn"
                onClick={() => {
                  setFilterSystem("all");
                  setFilterModule("all");
                  setFilterDistributor(
                    "all"
                  );
                  setFilterSite("all");
                  setSearchTerm("")
                }}
              >
                Clear
              </button>
            </div>

            {/* FILTER CONTROLS */}
            <div className="filter-controls">
              {/* SYSTEM */}
              <div className="customer-filter-group">
                <label>
                  Systems
                </label>

                <select
                  value={filterSystem}
                  onChange={(e) =>
                    setFilterSystem(
                      e.target.value
                    )
                  }
                >
                  <option value="all">
                    All Systems
                  </option>

                  {allSystem?.map(
                    (item) => (
                      <option
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* SITE */}
              <div className="customer-filter-group">
                <label>
                  Site
                </label>

                <select
                  value={filterSite}
                  onChange={(e) =>
                    setFilterSite(
                      e.target.value
                    )
                  }
                >
                  <option value="all">
                    All Sites
                  </option>

                  {siteOptions.map(
                    (site) => (
                      <option
                        key={site}
                        value={site}
                      >
                        {site}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/* MODULE */}
              <div className="customer-filter-group">
                <label>
                  Modules
                </label>

                <select
                  value={filterModule}
                  onChange={(e) =>
                    setFilterModule(
                      e.target.value
                    )
                  }
                >
                  <option value="all">
                    All Modules
                  </option>

                  {allModules?.map(
                    (module) => (
                      <option
                        key={module.id}
                        value={module.id}
                      >
                        {module.name}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>
          </div>

          {/* TABLE */}
          <div className="customers-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="loader-spinner"></div>

                <p>
                  Loading customers...
                </p>
              </div>
            ) : filteredCustomers.length ===
              0 ? (
              <div className="empty-state">
                <i className="fas fa-users"></i>

                <p>
                  No customers found
                </p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="customers-table">
                  <thead>
                    <tr>
                      <th>
                        Customer Name
                      </th>

                      {/* <th>Email</th>

                      <th>Phone</th>

                      <th>System</th>

                      <th>Site</th>

                      <th>Modules</th>

                      <th>Status</th> */}

                      <th style={{textAlign:"start"}}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedCustomers.map(
                      (
                        customer,
                        index
                      ) => {
                        const ownerName =
                          `${customer.ownerlastname || ""} ${
                            customer.ownerfirstname ||
                            ""
                          }`.trim();

                        const avatarChar =
                          ownerName
                            ?.charAt(0)
                            ?.toUpperCase();

                        const isActivated =
                          customer.eid ||
                          customer.owner_id;

                        return (
                          <tr
                            key={index}
                            className="customer-row"
                          >
                            <td className="customer-name">
                              {/* <span className="customer-avatar">
                                {avatarChar}
                              </span> */}

                              <span className="customer-name-text">
                                {ownerName || "N/A"}
                              </span>
                            </td>

                            {/* <td className="customer-email">
                              {customer.email ||
                                "—"}
                            </td>

                            <td className="customer-phone">
                              {customer.phone ||
                                "—"}
                            </td>

                            <td className="device-type">
                              <span className="device-badge">
                                {customer.device_type ||
                                  "—"}
                              </span>
                            </td>

                            <td className="customer-site">
                              {customer.site_name || "—"}
                            </td>

                            <td className="modules-column">
                              {customer.modules ||
                                "—"}
                            </td> */}

                            {/* <td className="customer-status">
                              <span
                                className={`status-badge ${
                                  isActivated
                                    ? "activated"
                                    : "not-activated"
                                }`}
                              >
                                {isActivated
                                  ? "Activated"
                                  : "Not Activated"}
                              </span>
                            </td> */}

                            <td className="action-buttons">
                              <button
                                className="action-btn view-btn"
                                title="View customer details"
                                onClick={() =>
                                  handleViewCustomerDetails(
                                    customer
                                  )
                                }
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              <button
                                className="action-btn grant-btn"
                                title="Grant module access"
                                onClick={() =>
                                  handleGrantModule(customer)
                                }
                              >
                                <i className="fas fa-puzzle-piece"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && filteredCustomers.length > 0 && (
              <Pagination
                currentPage={customerCurrentPage}
                totalPages={customerTotalPages}
                onPageChange={setCustomerCurrentPage}
              />
            )}
          </div>
        </div>
      </div>

      {/* GRANT MODULE MODAL */}
      <GrantModuleModal
        isOpen={grantModuleModal}
        toggle={toggleGrantModuleModal}
        customer={grantModuleCustomer}
        allModules={allModules}
        distributorId={userId}
        onSuccess={fetchCustomers}
      />

      {/* DETAILS MODAL */}
      <Modal
        isOpen={orderDetailsModal}
        toggle={toggleOrderDetailsModal}
        centered
        className="order-details-modal"
        size="xl"
      >
        <ModalHeader
          toggle={
            toggleOrderDetailsModal
          }
          style={{color:"#fff"}}
        >
          Customer Details
        </ModalHeader>

        <ModalBody>
          {selectedOrder && (
            <div className="customer-modal-content">
              {/* Original customer details */}
              <div className="details-grid">
                {/* Row 1: First Name, Last Name, Business */}
                <div className="form-row">
                  <div className="form-col-auto">
                    <strong>First Name:</strong>{" "}
                    {selectedOrder.ownerfirstname || "NA"}
                  </div>
                  <div className="form-col-auto">
                    <strong>Last Name:</strong>{" "}
                    {selectedOrder.ownerlastname || "NA"}
                  </div>
                  <div className="form-col-50">
                    <strong>Business:</strong>{" "}
                    {selectedOrder.business || "NA"}
                  </div>
                </div>

             

                {/* Row 3: Email, Phone, Password */}
                <div className="form-row">
                  <div className="form-col-50">
                    <strong>Email:</strong>{" "}
                    {selectedOrder.email || "NA"}
                  </div>
                  <div className="form-col-auto">
                    <strong>Phone:</strong>{" "}
                    {selectedOrder.phone || "NA"}
                  </div>
                  <div className="form-col-auto password-field">
                    <strong>Password:</strong>{" "}
                    <span className="password-value">
                      {showPassword
                        ? selectedOrder.password || "NA"
                        : "••••••••••"}
                    </span>
                    <button
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      <i className={`fas fa-eye${showPassword ? "-slash" : ""}`}></i>
                    </button>
                  </div>
                </div>
   {/* Row 2: Address 1 and Address 2 */}
                <div className="form-row">
                  <div className="form-col-50">
                    <strong>Address 1:</strong>{" "}
                    {selectedOrder.address1 || "NA"}
                  </div>
                  <div className="form-col-50">
                    <strong>Address 2:</strong>{" "}
                    {selectedOrder.address2 || "NA"}
                  </div>
                </div>

                <div>
               
                  <strong>City:</strong>{" "}
                  {selectedOrder.city || "NA"}
                </div>
                <div>
                  <strong>State:</strong>{" "}
                  {selectedOrder.state || "NA"}
                </div>
                <div>
                  <strong>Country:</strong>{" "}
                  {selectedOrder.country || "NA"}
                </div>
                  <div>
                  <strong>Postal Code:</strong>{" "}
                  {selectedOrder.zipcode || "NA"}
                </div>
                <div className="form-row">
              
                                  {/* <div className="form-col-50">

                  <strong>Gender:</strong>{" "}
                  {selectedOrder.gender || "NA"}
                </div> */}
                                <div className="form-col-50">

                    <strong>System:</strong>{" "}
                    {selectedOrder.device_type || "NA"}
                  </div>
                </div>

                {/* Row 4: System and Modules */}
                <div className="form-row">
                 
                  <div className="form-col-100">
                    <strong>Modules:</strong>{" "}
                    {selectedOrder.modules || "NA"}
                  </div>
                </div>
              </div>

              {/* Registrations Table */}
              <div className="registrations-table-section">
                <h5 className="registrations-title">
                  <i className="fas fa-list-alt"></i>
                  Devices ({(selectedOrder.allRegistrations || []).length})
                </h5>

                {(selectedOrder.allRegistrations || []).length === 0 ? (
                  <p className="no-registrations">No device found.</p>
                ) : (
                  <table className="registrations-table">
                    <thead>
                      <tr>
                        <th>Device #</th>
                        <th>Internal ID</th>
                        <th>Order ID</th>
                        <th>Date Activated</th>
                        <th>Expires On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrder.allRegistrations || []).map((reg, idx) => (
                        <tr key={idx}>
                          <td>Device #{idx + 1}</td>
                          <td>{reg.internal_id || "N/A"}</td>
                          <td>{reg.order_id || "N/A"}</td>
                          <td>{formatTimestamp(reg.date_activated)}</td>
                          <td>{formatTimestamp(reg.expires_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <button
            onClick={
              toggleOrderDetailsModal
            }
            className="btn-primary-modern"
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ManageCustomers;